import { motion } from "framer-motion";
import { useLocation } from "react-router-dom";
import { FiArrowUpRight } from "react-icons/fi";
import { UseNavItems } from "../../lib/UseNavItems";
import { useThrottle } from "../../../utils/performance";
import { useState, useEffect, useRef, useCallback, useMemo } from "react";

const DesktopNavigation = () => {
  const navItems = UseNavItems;
  const timeoutRef = useRef(null);
  const location = useLocation();
  const [activeSection, setActiveSection] = useState("#hello");

  // Performance: Memoize sections array
  const sections = useMemo(
    () => [
      "hello",
      "experiences",
      "skills",
      "projects",
      "strengths",
      "contact",
    ],
    [],
  );

  useEffect(() => {
    // Set initial active section based on current hash or default to hello
    const currentHash = location.hash || "#hello";
    setActiveSection(currentHash);

    if (!location.hash) {
      window.location.hash = "#hello";
    }
  }, [location]);

  // Debounced function to update active section
  const debouncedSetActiveSection = useCallback((sectionId) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(() => {
      setActiveSection(sectionId);
      // Update URL hash without triggering page jump
      if (window.location.hash !== sectionId) {
        window.history.replaceState(null, null, sectionId);
      }
    }, 100); // Reduced delay for better responsiveness
  }, []);

  const visibleSections = useRef(new Set());

  useEffect(() => {
    const observerOptions = {
      root: null,
      rootMargin: "-10% 0px -10% 0px", // Reduced margins
      threshold: [0, 0.1, 0.25, 0.5, 0.75, 1], // More thresholds for better tracking
    };

    const observer = new IntersectionObserver((entries) => {
      // Update visibility set
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          visibleSections.current.add(entry.target.id);
        } else {
          visibleSections.current.delete(entry.target.id);
        }
      });

      let bestCandidate = null;
      let maxVisibleHeight = 0;

      // Find best candidate from all currently visible sections based on live DOM rects
      visibleSections.current.forEach((id) => {
        const element = document.getElementById(id);
        if (!element) return;

        const rect = element.getBoundingClientRect();
        const viewportHeight = window.innerHeight;

        // Calculate how much vertical space this section takes up in the viewport
        const visibleTop = Math.max(rect.top, 0);
        const visibleBottom = Math.min(rect.bottom, viewportHeight);
        const visibleHeight = Math.max(0, visibleBottom - visibleTop);

        if (visibleHeight > maxVisibleHeight) {
          maxVisibleHeight = visibleHeight;
          bestCandidate = id;
        }
      });

      // Fallback: if nothing is clearly visible (e.g. fast scrolling), use the first one in the viewport
      if (!bestCandidate && visibleSections.current.size > 0) {
        bestCandidate = Array.from(visibleSections.current)[0];
      }

      if (bestCandidate) {
        debouncedSetActiveSection(`#${bestCandidate}`);
      }
    }, observerOptions);
    // Retry mechanism to ensure elements are observed even if they mount later
    let intervalId;
    const observedSections = new Set();

    const observeElements = () => {
      let allObserved = true;
      sections?.forEach((sectionId) => {
        if (observedSections.has(sectionId)) return;

        const section = document.getElementById(sectionId);
        if (section) {
          observer.observe(section);
          observedSections.add(sectionId);
        } else {
          allObserved = false;
        }
      });
      return allObserved;
    };

    if (!observeElements()) {
      intervalId = setInterval(() => {
        if (observeElements()) {
          clearInterval(intervalId);
        }
      }, 500);
    }

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
      sections?.forEach((sectionId) => {
        const section = document.getElementById(sectionId);
        if (section) {
          observer.unobserve(section);
        }
      });

      // Cleanup timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [debouncedSetActiveSection, sections]);

  // Performance: Throttled scroll handler
  const throttledScrollTo = useThrottle((id) => {
    const section = document.getElementById(id);
    if (section) {
      section.scrollIntoView({ behavior: "smooth" });
      setActiveSection(`#${id}`);
      window.history.pushState(null, "", `#${id}`);
    }
  }, 100);

  const handleScrollTo = (id) => {
    throttledScrollTo(id);
  };

  const itemRefs = useRef([]);
  const [indicatorStyle, setIndicatorStyle] = useState({ top: 0, height: 0 });

  useEffect(() => {
    const activeIndex = navItems.findIndex(
      (item) => item.path === activeSection,
    );
    if (activeIndex !== -1 && itemRefs.current[activeIndex]) {
      const el = itemRefs.current[activeIndex];
      setIndicatorStyle({
        top: el.offsetTop,
        height: el.offsetHeight,
      });
    }
  }, [activeSection, navItems]);

  return (
    <div className="px-3 py-1 md:py-3 lg:py-3 xl:py-1.5 2xl:py-3 text-gray-100 textFont lg:block md:block hidden">
      <ul className="pl-4 space-y-2.5 text-[15px] xl:pt-0 2xl:pt-9 md:pt-3 lg:pt-4 relative">
        {/* Continuous Active Indicator (Water drop smooth motion) */}
        <motion.div
          className="absolute right-0 flex items-center z-10 pointer-events-none"
          animate={{ top: indicatorStyle.top, height: indicatorStyle.height }}
          transition={{
            mass: 0.8,
            damping: 20,
            type: "spring",
            stiffness: 280,
          }}
        >
          <div className="active-circle w-[22px] h-[22px] rounded-full p-[12px] border border-dashed border-[#22D4FD] relative">
            <div className="h-2 w-2 bg-[#22D4FD] rounded-full absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 animate-pulse"></div>
          </div>
        </motion.div>

        {navItems?.map((item, index) => (
          <li
            key={index + 1}
            className="relative"
            ref={(el) => (itemRefs.current[index] = el)}
          >
            {item?.external ? (
              <a
                target="blank"
                href={item?.path}
                className="w-full flex items-center justify-between relative z-20 group"
              >
                <div className="flex items-center justify-between w-full px-2 py-2 rounded-md transition-all duration-200 uppercase text-[#22D4FD] bg-[#22D4FD]/10 border border-[#22D4FD]/30 hover:border-[#22D4FD] hover:bg-[#22D4FD]/20 shadow-[0_0_10px_rgba(34,212,253,0.1)]">
                  <div className="flex items-center space-x-3">
                    {item?.icon}
                    <span>{item?.label}</span>
                  </div>
                  <FiArrowUpRight className="text-xs group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                </div>
              </a>
            ) : (
              <button
                onClick={() => handleScrollTo(item?.path?.replace("#", ""))}
                className="w-full flex items-center justify-between relative z-20"
              >
                <div
                  className={`flex items-center px-2 py-3 xl:py-2 2xl:py-3 space-x-3 rounded-md transition-colors uppercase duration-200 ${
                    activeSection === item?.path
                      ? "text-[#22D4FD]"
                      : "text-gray-400 hover:text-gray-200"
                  }`}
                >
                  {item?.icon}
                  <span>{item?.label}</span>
                </div>
                {/* The active circle is now rendered as a single continuous element above */}
              </button>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
};

export default DesktopNavigation;
